<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    tabindex="-1"
    v-if="isVisible"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("SavedReports") }}</h5>
          <div v-if="isAdmin" class="d-flex align-items-center me-2">
            <input
              ref="reportConfigFileInput"
              type="file"
              accept=".json,application/json"
              class="d-none"
              @change="handleImportReportConfigFile"
            />
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="openImportReportConfig"
            >
              <i class="fas fa-file-import me-1"></i>
              {{ $t("ImportReportConfig") }}
            </button>
          </div>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <ContentSkeleton v-if="isLoading" variant="table" :rows="5" :cols="5" padded />
          <div v-else-if="reports.length === 0" class="text-center py-4">
            <p class="text-muted">{{ $t("NoSavedReportsFound") }}</p>
          </div>
          <div v-else>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>{{ $t("Name") }}</th>
                    <th>{{ $t("Description") }}</th>
                    <th>{{ $t("Visibility") }}</th>
                    <th>{{ $t("CreatedBy") }}</th>
                    <th>{{ $t("Created") }}</th>
                    <th>{{ $t("Actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="report in reports" :key="report._id">
                    <td>
                      <i :class="'fas fa-' + (report.icon || 'file-alt')" class="me-2 text-primary"></i>
                      <strong>{{ report.name }}</strong>
                    </td>
                    <td>
                      <small class="text-muted">{{
                        report.description || "-"
                      }}</small>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          report.isPublic
                            ? 'bg-success'
                            : 'bg-secondary'
                        "
                      >
                        {{ report.isPublic ? $t("Public") : $t("Private") }}
                      </span>
                    </td>
                    <td>
                      <small>{{
                        report.createdBy?.name || $t("Unknown")
                      }}</small>
                    </td>
                    <td>
                      <small>{{
                        formatDate(report.createdAt)
                      }}</small>
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-primary me-1"
                        @click="loadReport(report)"
                        :title="$t('LoadReport')"
                      >
                        <i class="fas fa-play"></i> {{ $t("Load") }}
                      </button>
                      <button
                        v-if="canEdit(report)"
                        class="btn btn-sm btn-warning me-1"
                        @click="editReport(report)"
                        :title="$t('EditReport')"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        v-if="canDelete(report)"
                        class="btn btn-sm btn-danger"
                        @click="deleteReport(report)"
                        :title="$t('DeleteReport')"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                      <button
                        v-if="isAdmin"
                        class="btn btn-sm btn-outline-secondary ms-1"
                        @click="exportReportConfig(report)"
                        :title="$t('ExportReportConfig')"
                      >
                        <i class="fas fa-file-download"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">
            {{ $t("Close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import {
  REPORT_ICONS,
  fetchAssignableUsers,
  openEditReportDialog,
} from "@/utils/reportEditPrompt";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "ReportsListModal",
  components: { ContentSkeleton },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    currentUserId: {
      type: String,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reports: [],
      isLoading: false,
      systemUsers: [],
      loadingSystemUsers: false,
      availableIcons: REPORT_ICONS,
    };
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.loadReports();
      }
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    openImportReportConfig() {
      if (!this.isAdmin) return;
      this.$refs.reportConfigFileInput?.click();
    },
    buildExportableReportConfig(report) {
      return {
        type: "gotrading_saved_report_config",
        version: 1,
        exportedAt: new Date().toISOString(),
        modelName: report?.modelName || this.modelName,
        viewId: report?.viewId || null,
        report: {
          name: report?.name || "",
          description: report?.description || "",
          icon: report?.icon || "file-alt",
          isPublic: !!report?.isPublic,
          columns:
            report?.columns && typeof report.columns === "object"
              ? report.columns
              : {},
          columnOrder: Array.isArray(report?.columnOrder) ? report.columnOrder : [],
          columnWidths:
            report?.columnWidths && typeof report.columnWidths === "object"
              ? report.columnWidths
              : {},
          columnLabels:
            report?.columnLabels && typeof report.columnLabels === "object"
              ? report.columnLabels
              : {},
          columnDescriptions:
            report?.columnDescriptions &&
            typeof report.columnDescriptions === "object"
              ? report.columnDescriptions
              : {},
          columnEmphasis:
            report?.columnEmphasis && typeof report.columnEmphasis === "object"
              ? report.columnEmphasis
              : {},
          columnTotals:
            report?.columnTotals && typeof report.columnTotals === "object"
              ? report.columnTotals
              : {},
          columnLayout:
            report?.columnLayout && typeof report.columnLayout === "object"
              ? report.columnLayout
              : { groups: [], merges: [] },
          pinnedColumnKeys: Array.isArray(report?.pinnedColumnKeys)
            ? report.pinnedColumnKeys
            : [],
          filters:
            report?.filters && typeof report.filters === "object"
              ? report.filters
              : {},
          selectedFilterFields:
            report?.selectedFilterFields &&
            typeof report.selectedFilterFields === "object"
              ? report.selectedFilterFields
              : {},
          sortField: report?.sortField || "",
          sortOrder: report?.sortOrder === "desc" ? "desc" : "asc",
          pagination:
            report?.pagination && typeof report.pagination === "object"
              ? report.pagination
              : { page: 1, perPage: 20 },
        },
      };
    },
    sanitizeImportedConfig(payload) {
      if (!payload || typeof payload !== "object") return null;

      const source = payload.report && typeof payload.report === "object"
        ? payload.report
        : payload;

      const asObject = (value) =>
        value && typeof value === "object" && !Array.isArray(value) ? value : {};

      return {
        modelName:
          typeof payload.modelName === "string" ? payload.modelName.trim() : "",
        viewId:
          typeof payload.viewId === "string" && payload.viewId.trim()
            ? payload.viewId.trim()
            : null,
        name: typeof source.name === "string" ? source.name.trim() : "",
        description:
          typeof source.description === "string" ? source.description.trim() : "",
        icon: typeof source.icon === "string" && source.icon.trim()
          ? source.icon.trim()
          : "file-alt",
        columns: asObject(source.columns),
        columnOrder: Array.isArray(source.columnOrder)
          ? source.columnOrder
              .filter((value) => typeof value === "string" && value.trim())
              .map((value) => value.trim())
          : [],
        columnWidths: asObject(source.columnWidths),
        columnLabels: asObject(source.columnLabels),
        columnDescriptions: asObject(source.columnDescriptions),
        columnEmphasis: asObject(source.columnEmphasis),
        columnTotals: asObject(source.columnTotals),
        columnLayout:
          source.columnLayout &&
          typeof source.columnLayout === "object" &&
          !Array.isArray(source.columnLayout)
            ? source.columnLayout
            : { groups: [], merges: [] },
        pinnedColumnKeys: Array.isArray(source.pinnedColumnKeys)
          ? source.pinnedColumnKeys
              .filter((value) => typeof value === "string" && value.trim())
              .map((value) => value.trim())
          : [],
        filters: asObject(source.filters),
        selectedFilterFields: asObject(source.selectedFilterFields),
        sortField:
          typeof source.sortField === "string" ? source.sortField.trim() : "",
        sortOrder: source.sortOrder === "desc" ? "desc" : "asc",
        pagination: {
          page: 1,
          perPage:
            Number(source?.pagination?.perPage) > 0
              ? Number(source.pagination.perPage)
              : 20,
        },
      };
    },
    async exportReportConfig(report) {
      if (!this.isAdmin) return;

      try {
        const exportPayload = this.buildExportableReportConfig(report);
        const jsonContent = JSON.stringify(exportPayload, null, 2);
        const blob = new Blob([jsonContent], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeName = (report?.name || "report")
          .toLowerCase()
          .replace(/[^a-z0-9-_]+/g, "-")
          .replace(/^-+|-+$/g, "");

        a.href = url;
        a.download = `${safeName || "report"}-config.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting report config:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("ErrorExportingReportConfig"),
        });
      }
    },
    async handleImportReportConfigFile(event) {
      if (!this.isAdmin) return;

      const file = event?.target?.files?.[0];
      if (!file) return;

      try {
        const fileContent = await file.text();
        const parsed = JSON.parse(fileContent);
        const imported = this.sanitizeImportedConfig(parsed);
        if (!imported) {
          throw new Error(this.$t("InvalidReportConfigFile"));
        }

        if (imported.modelName && imported.modelName !== this.modelName) {
          const confirmMismatch = await Swal.fire({
            icon: "warning",
            title: this.$tn("ModelMismatch"),
            text: this.$tn("ReportConfigModelMismatch"),
            showCancelButton: true,
            confirmButtonText: this.$tn("Continue"),
            cancelButtonText: this.$tn("Cancel"),
          });
          if (!confirmMismatch.isConfirmed) {
            return;
          }
        }

        const defaultName = imported.name || file.name.replace(/\.json$/i, "");
        const namePrompt = await Swal.fire({
          title: this.$tn("ImportReportConfig"),
          input: "text",
          inputLabel: this.$tn("ReportName"),
          inputValue: defaultName,
          inputPlaceholder: this.$tn("EnterReportName"),
          showCancelButton: true,
          confirmButtonText: this.$tn("Import"),
          cancelButtonText: this.$tn("Cancel"),
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return this.$tn("PleaseEnterReportName");
            }
            return null;
          },
        });

        if (!namePrompt.isConfirmed) return;

        const payload = {
          name: namePrompt.value.trim(),
          description: imported.description || "",
          modelName: this.modelName,
          viewId: imported.viewId,
          isPublic: false,
          icon: imported.icon || "file-alt",
          columns: imported.columns,
          columnOrder: imported.columnOrder,
          columnWidths: imported.columnWidths,
          columnLabels: imported.columnLabels,
          columnDescriptions: imported.columnDescriptions,
          columnEmphasis: imported.columnEmphasis,
          columnTotals: imported.columnTotals,
          columnLayout: imported.columnLayout,
          pinnedColumnKeys: imported.pinnedColumnKeys,
          filters: imported.filters,
          selectedFilterFields: imported.selectedFilterFields,
          sortField: imported.sortField,
          sortOrder: imported.sortOrder,
          pagination: imported.pagination,
        };

        await wencesApi.post("/api/gotrading/reports", payload);

        Swal.fire({
          icon: "success",
          title: this.$tn("success"),
          text: this.$tn("ReportConfigImportedSuccessfully"),
          timer: 2000,
          showConfirmButton: false,
        });

        await this.loadReports();
      } catch (error) {
        console.error("Error importing report config:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text:
            error?.response?.data?.error ||
            error?.message ||
            this.$tn("ErrorImportingReportConfig"),
        });
      } finally {
        if (this.$refs.reportConfigFileInput) {
          this.$refs.reportConfigFileInput.value = "";
        }
      }
    },
    async loadSystemUsers() {
      if (!this.isAdmin || this.loadingSystemUsers) return;
      this.loadingSystemUsers = true;
      try {
        this.systemUsers = await fetchAssignableUsers();
      } finally {
        this.loadingSystemUsers = false;
      }
    },
    async loadReports() {
      this.isLoading = true;
      try {
        if (this.isAdmin && this.systemUsers.length === 0) {
          await this.loadSystemUsers();
        }
        const { data } = await wencesApi.get(
          `/api/gotrading/reports?modelName=${this.modelName}`
        );
        this.reports = data;
      } catch (error) {
        console.error("Error loading reports:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("ErrorLoadingReports"),
        });
      } finally {
        this.isLoading = false;
      }
    },
    loadReport(report) {
      this.$emit("load-report", report);
      this.close();
    },
    canEdit(report) {
      if (this.isAdmin) return true;
      if (!this.currentUserId || !report.createdBy) return false;
      return String(report.createdBy._id || report.createdBy) === String(this.currentUserId);
    },
    canDelete(report) {
      if (this.isAdmin) return true;
      if (!this.currentUserId || !report.createdBy) return false;
      return String(report.createdBy._id || report.createdBy) === String(this.currentUserId);
    },
    async editReport(report) {
      if (this.isAdmin && this.systemUsers.length === 0) {
        await this.loadSystemUsers();
      }
      const updated = await openEditReportDialog({
        report,
        t: this.$t.bind(this),
        isAdmin: this.isAdmin,
        systemUsers: this.systemUsers,
      });
      if (updated) {
        this.loadReports();
      }
    },
    async deleteReport(report) {
      const result = await Swal.fire({
        title: this.$tn("AreYouSure"),
        text: this.$tn("ConfirmDeleteReport").replace("{name}", report.name),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$tn("YesDeleteIt"),
        cancelButtonText: this.$tn("Cancel"),
      });

      if (result.isConfirmed) {
        try {
          await wencesApi.delete(`/api/gotrading/reports/${report._id}`);
          Swal.fire({
            icon: "success",
            title: this.$tn("Deleted"),
            text: this.$tn("ReportDeletedSuccessfully"),
            timer: 2000,
            showConfirmButton: false,
          });
          this.loadReports();
        } catch (error) {
          console.error("Error deleting report:", error);
          Swal.fire({
            icon: "error",
            title: this.$tn("error"),
            text: error.response?.data?.error || this.$tn("ErrorDeletingReport"),
          });
        }
      }
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal.show {
  display: block;
}
</style>

<style>
/* Estilos globales para el selector de iconos en SweetAlert */
.icon-selector-container {
  text-align: left;
}

.selected-icon-display {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.icon-grid-edit {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 0.25rem;
  max-height: 180px;
  overflow-y: auto;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #495057;
  padding: 0;
}

.icon-btn:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.icon-btn.selected {
  background-color: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.icon-btn i {
  font-size: 0.875rem;
}
</style>

